import React, { useState, useEffect, useCallback } from 'react';
import { API_CONFIG, siteConfig } from '../config';

const DateRangePicker = ({ onAvailabilityChange, propertyData, isDarkMode = false }) => {
  const [dateRange, setDateRange] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    console.log('Initializing currentMonth to:', now.getMonth(), now.getFullYear());
    return now;
  });
  const [availability, setAvailability] = useState(null);
  const [pricingData, setPricingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [userHasInteracted, setUserHasInteracted] = useState(false);
  const [checkedDateRange, setCheckedDateRange] = useState('');
  const [blockedDates, setBlockedDates] = useState({});
  const [monthAvailability, setMonthAvailability] = useState({});

  // Set default dates (next weekend)
  useEffect(() => {
    const today = new Date();
    const nextWeekend = new Date(today);
    nextWeekend.setDate(today.getDate() + (7 - today.getDay() + 5) % 7); // Next Friday
    
    const endDate = new Date(nextWeekend);
    endDate.setDate(nextWeekend.getDate() + 3); // 3 nights
    
    setSelectedStartDate(nextWeekend);
    setSelectedEndDate(endDate);
    setDateRange(`${nextWeekend.toISOString().split('T')[0]} - ${endDate.toISOString().split('T')[0]}`);
  }, []);

  const fetchAvailability = useCallback(async (start, end) => {
    if (!start || !end || isFetching) {
      console.log('Skipping fetch - missing dates or already fetching');
      return;
    }

    console.log('Starting availability fetch for:', start, 'to', end);
    setIsFetching(true);
    setLoading(true);
    setError(null);

    // No timeout fallback - we only want real API data

    try {
      // Real API call to Nightsbridge availgrid (using working worker format)
      const requestBody = {
        bbid: siteConfig.propertyBBID.toString(), // J&B Lodge BBID (as string per API docs)
        startdate: start,
        enddate: end,
        strictsearch: false, // Include minimum stay information
        includepackages: true // Include pricing data
      };

      console.log('Sending availability request:', requestBody);
      
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Cache-Control': 'no-cache'
      };
      
      const response = await fetch(API_CONFIG.availgridUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        console.error('API Response not OK:', {
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries())
        });
        throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Availability data received:', data);
      console.log('Data structure check:', {
        success: data.success,
        hasData: !!data.data,
        roomtypes: data.data?.roomtypes,
        firstRoom: data.data?.roomtypes?.[0]
      });
      setAvailability(data);
      onAvailabilityChange(data);

      // Fetch pricing data for available rooms
      if (data.data?.roomtypes) {
        await fetchPricingData(start, end, data.data.roomtypes);
      }

      setTimeout(() => {
        setLoading(false);
        setIsFetching(false);
      }, 2000);
      
    } catch (err) {
      console.error('Error fetching availability:', err);
      setError('Error fetching availability. Please try again.');
      setLoading(false);
      setIsFetching(false);
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  }, [onAvailabilityChange, isFetching]);

  const fetchPricingData = useCallback(async (startDate, endDate, roomTypes) => {
    try {
      console.log('Fetching pricing data for available rooms...');
      
      // Get available room IDs
      const availableRoomIds = roomTypes
        .filter(room => {
          if (room.availability && Array.isArray(room.availability)) {
            return room.availability.every(day => day.roomsfree === true);
          }
          return room.available !== false;
        })
        .map(room => room.roomtypeid || room.rtid);

      if (availableRoomIds.length === 0) {
        console.log('No available rooms to fetch pricing for');
        return;
      }

      // Fetch pricing for each available room
      const pricingPromises = availableRoomIds.map(async (roomId) => {
        const pricingRequestBody = {
          bbid: siteConfig.propertyBBID.toString(),
          startdate: startDate,
          enddate: endDate,
          bbrtid: roomId, // Use bbrtid for room type ID per API docs
          strictsearch: false, // Include minimum stay information
          includepackages: true // Include pricing data
        };

        const headers = {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Cache-Control': 'no-cache'
        };

        try {
          const response = await fetch(API_CONFIG.availgridUrl, {
            method: 'POST',
            headers,
            body: JSON.stringify(pricingRequestBody)
          });

          if (response.ok) {
            const pricingData = await response.json();
            console.log(`Pricing data for room ${roomId}:`, pricingData);
            return { roomId, pricingData };
          } else {
            console.error(`Failed to fetch pricing for room ${roomId}:`, response.status);
            return { roomId, pricingData: null };
          }
        } catch (error) {
          console.error(`Error fetching pricing for room ${roomId}:`, error);
          return { roomId, pricingData: null };
        }
      });

      const pricingResults = await Promise.all(pricingPromises);
      const pricingMap = {};
      
      pricingResults.forEach(({ roomId, pricingData }) => {
        if (pricingData && pricingData.data) {
          pricingMap[roomId] = pricingData.data;
        }
      });

      console.log('Pricing data map:', pricingMap);
      setPricingData(pricingMap);
      
    } catch (error) {
      console.error('Error fetching pricing data:', error);
    }
  }, []);

  useEffect(() => {
    if (selectedStartDate && selectedEndDate && selectedStartDate !== selectedEndDate && userHasInteracted) {
      const currentDateRange = `${selectedStartDate.toISOString().split('T')[0]}-${selectedEndDate.toISOString().split('T')[0]}`;
      
      // Only check if we haven't already checked this exact date range
      if (checkedDateRange !== currentDateRange) {
        console.log('Triggering availability check for:', selectedStartDate.toISOString().split('T')[0], 'to', selectedEndDate.toISOString().split('T')[0]);
        setCheckedDateRange(currentDateRange);
        fetchAvailability(selectedStartDate.toISOString().split('T')[0], selectedEndDate.toISOString().split('T')[0]);
      }
    }
  }, [selectedStartDate, selectedEndDate, fetchAvailability, userHasInteracted, checkedDateRange]);

  const handleDateClick = (date) => {
    setUserHasInteracted(true); // Mark that user has interacted
    
    if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
      // First selection or new selection
      setSelectedStartDate(date);
      setSelectedEndDate(null);
      setCheckedDateRange(''); // Reset checked date range for new selection
    } else {
      // Second selection
      if (date < selectedStartDate) {
        // If second date is before first, swap them
        setSelectedEndDate(selectedStartDate);
        setSelectedStartDate(date);
        setCheckedDateRange(''); // Reset checked date range for new selection
      } else {
        setSelectedEndDate(date);
        setCheckedDateRange(''); // Reset checked date range for new selection
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDisplayDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getNightsCount = () => {
    if (!selectedStartDate || !selectedEndDate) return 0;
    return Math.ceil((selectedEndDate - selectedStartDate) / (1000 * 60 * 60 * 24));
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const isDateInRange = (date) => {
    if (!selectedStartDate || !selectedEndDate) return false;
    return date >= selectedStartDate && date <= selectedEndDate;
  };

  const isDateSelected = (date) => {
    if (!date) return false;
    return (selectedStartDate && date.getTime() === selectedStartDate.getTime()) ||
           (selectedEndDate && date.getTime() === selectedEndDate.getTime());
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date.getTime() === today.getTime();
  };

  const isDateDisabled = (date) => {
    if (!date) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Check if date is in the past
    if (date < today) return true;
    
    // Check if date is blocked (no availability)
    const dateKey = date.toISOString().split('T')[0];
    if (blockedDates[dateKey]) {
      console.log('Date disabled (blocked):', dateKey);
      return true;
    }
    
    return false;
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  // Check availability for the current month
  const checkMonthAvailability = useCallback(async (month, year) => {
    // Convert from 0-based (JavaScript) to 1-based (API) month
    const apiMonth = month + 1;
    const monthKey = `${year}-${apiMonth.toString().padStart(2, '0')}`;
    
    console.log('checkMonthAvailability called for:', monthKey, 'monthAvailability keys:', Object.keys(monthAvailability));
    
    // Skip if we already have data for this month
    if (monthAvailability[monthKey]) {
      console.log('Already have data for', monthKey);
      return;
    }
    
    console.log('Fetching availability for API month:', apiMonth, 'JavaScript month:', month);
    
    try {
      // Get first and last day of the month
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      
      const requestBody = {
        bbid: 18993,
        startdate: firstDay.toISOString().split('T')[0],
        enddate: lastDay.toISOString().split('T')[0]
      };

      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Cache-Control': 'no-cache'
      };
      
      if (API_CONFIG.apiKey && API_CONFIG.secret) {
        const credentials = `${API_CONFIG.apiKey}:${API_CONFIG.secret}`;
        const encodedCredentials = btoa(credentials);
        headers['Authorization'] = `Basic ${encodedCredentials}`;
      }
      
      const response = await fetch(API_CONFIG.availgridUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const data = await response.json();
        setMonthAvailability(prev => ({
          ...prev,
          [monthKey]: data
        }));
        
        // Process blocked dates
        if (data.data?.roomtypes) {
          const blocked = {};
          console.log('Processing room availability for month:', monthKey);
          data.data.roomtypes.forEach(room => {
            if (room.availability && Array.isArray(room.availability)) {
              room.availability.forEach((day, index) => {
                // Use the API's date directly if available, otherwise calculate
                let dateKey;
                if (day.date) {
                  // If API provides the actual date, use it
                  dateKey = day.date;
                } else {
                  // Calculate date and adjust for timezone
                  const date = new Date(firstDay.getFullYear(), firstDay.getMonth(), firstDay.getDate() + index);
                  // Subtract one day to account for timezone offset
                  date.setDate(date.getDate() - 1);
                  dateKey = date.toISOString().split('T')[0];
                }
                
                if (!day.roomsfree) {
                  blocked[dateKey] = true;
                  console.log('Blocked date:', dateKey, 'for API day:', index + 1, 'day object:', day);
                }
              });
            }
          });
          console.log('Total blocked dates for', monthKey, ':', Object.keys(blocked).length);
          setBlockedDates(prev => ({ ...prev, ...blocked }));
        }
      }
    } catch (error) {
      console.log('Could not fetch month availability:', error);
    }
  }, [monthAvailability, API_CONFIG]);

  // Check availability when month changes, but only if calendar is shown
  useEffect(() => {
    if (showCalendar) {
      console.log('Calendar opened, checking availability for:', currentMonth.getMonth(), currentMonth.getFullYear());
      checkMonthAvailability(currentMonth.getMonth(), currentMonth.getFullYear());
    }
  }, [currentMonth, checkMonthAvailability, showCalendar]);

  // Also check availability when calendar first opens
  useEffect(() => {
    if (showCalendar) {
      console.log('Calendar shown, fetching current month availability');
      checkMonthAvailability(currentMonth.getMonth(), currentMonth.getFullYear());
    }
  }, [showCalendar, checkMonthAvailability]);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const days = getDaysInMonth(currentMonth);

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Check Availability</h2>
        <p className="text-gray-600">Select your dates to see room availability</p>
      </div>

      {/* Single Date Range Input */}
      <div className="max-w-md mx-auto mb-6">
        <div className="relative">
          <div className="relative">
            <input
              type="text"
              id="dateRange"
              value={dateRange}
              readOnly
              onClick={() => setShowCalendar(!showCalendar)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 cursor-pointer text-center"
              placeholder="Select dates"
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Calendar Popup */}
      {showCalendar && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowCalendar(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={prevMonth}
                className="p-1 rounded"
                style={{ backgroundColor: '#f6f2ec' }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                </svg>
              </button>
              <h3 className="text-lg font-semibold">
                {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </h3>
              <button
                onClick={nextMonth}
                className="p-1 rounded"
                style={{ backgroundColor: '#f6f2ec' }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                </svg>
              </button>
            </div>
            
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-sm font-medium text-gray-500 py-1">
                  {day}
                </div>
              ))}
            </div>
            
            <div className="grid grid-cols-7 gap-1 h-64">
              {days.map((day, index) => (
                <button
                  key={index}
                  onClick={() => {
                    if (day && !isDateDisabled(day)) {
                      handleDateClick(day);
                      // Update the date range display
                      if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
                        // First selection or new selection
                        setDateRange(day.toISOString().split('T')[0]);
                      } else {
                        // Second selection (checkout date) - close calendar and start search
                        const start = selectedStartDate.toISOString().split('T')[0];
                        const end = day.toISOString().split('T')[0];
                        setDateRange(`${start} - ${end}`);
                        setShowCalendar(false); // Close calendar when checkout date is selected
                      }
                    }
                  }}
                  disabled={isDateDisabled(day)}
                  className={`
                    p-2 text-sm rounded hover:bg-blue-50 focus:outline-none flex items-center justify-center h-8
                    ${!day ? 'invisible' : ''}
                    ${isDateDisabled(day) ? 'bg-gray-100 text-gray-500 cursor-not-allowed line-through' : 'text-gray-700 hover:text-blue-600'}
                    ${isDateSelected(day) ? 'bg-black text-white hover:bg-gray-800 hover:text-[#ffcc00] rounded-full w-[35px] h-[35px] focus:ring-2 focus:ring-black' : ''}
                    ${isDateInRange(day) && !isDateSelected(day) ? 'bg-gray-100 text-gray-700' : ''}
                    ${isToday(day) ? 'border-2 border-black rounded-full w-[35px] h-[35px]' : ''}
                    ${!isDateSelected(day) ? 'focus:ring-2 focus:ring-blue-500' : ''}
                  `}
                >
                  {day ? day.getDate() : ''}
                </button>
              ))}
            </div>
            

          </div>
        </div>
      )}



      {/* Loading State */}
      {loading && (
        <div className="text-center py-6 bg-blue-50 rounded-lg border border-blue-200">
          <div className="inline-flex items-center mb-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
            <span className="text-blue-800 font-medium text-lg">
              {isFetching ? 'Checking room availability...' : 'Loading availability data...'}
            </span>
          </div>
          <p className="text-blue-600 text-sm">
            Please wait while we connect to the availability service.
          </p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center">
              <svg className="w-6 h-6 text-red-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <span className="text-red-800 font-medium text-lg">Error loading availability data</span>
            </div>
            <button
              onClick={() => {
                setError(null);
                if (selectedStartDate && selectedEndDate) {
                  fetchAvailability(selectedStartDate.toISOString().split('T')[0], selectedEndDate.toISOString().split('T')[0]);
                }
              }}
              className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
          <p className="text-red-700 text-sm">We couldn't connect to the availability service. Please check your connection and try again.</p>
        </div>
      )}

      {/* Availability Summary */}
      {availability && !loading && !error && (
        <div className={`border rounded-lg p-4 mb-6 ${
          (() => {
            if (!availability.data?.roomtypes) return 'bg-green-50 border-green-200';
            
            const roomTypes = availability.data.roomtypes;
            const availableRooms = roomTypes.filter(room => {
              if (room.availability && Array.isArray(room.availability)) {
                return room.availability.every(day => day.roomsfree === true);
              }
              return room.available !== false;
            });
            
            if (availableRooms.length === 0) {
              return 'bg-yellow-50 border-yellow-200';
            } else if (availableRooms.length === roomTypes.length) {
              return 'bg-green-50 border-green-200';
            } else {
              return 'bg-blue-50 border-blue-200';
            }
          })()
        }`}>
          <div className="flex items-center justify-center">
            {(() => {
              if (!availability.data?.roomtypes) {
                return (
                  <>
                    <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <span className="text-green-800 font-medium">Availability data loaded</span>
                  </>
                );
              }
              
              const roomTypes = availability.data.roomtypes;
              const availableRooms = roomTypes.filter(room => {
                if (room.availability && Array.isArray(room.availability)) {
                  return room.availability.every(day => day.roomsfree === true);
                }
                return room.available !== false;
              });
              
              if (availableRooms.length === 0) {
                return (
                  <>
                    <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                    </svg>
                    <span className="text-yellow-800 font-medium">No rooms available for selected dates</span>
                  </>
                );
              } else if (availableRooms.length === roomTypes.length) {
                return (
                  <>
                    <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <span className="text-green-800 font-medium">
                      Rooms are available for selected dates,{' '}
                      <button 
                        onClick={() => {
                          document.querySelector('.space-y-8')?.scrollIntoView({ 
                            behavior: 'smooth',
                            block: 'start'
                          });
                        }}
                        className="underline hover:text-green-900 font-semibold"
                      >
                        see rooms
                      </button>
                    </span>
                  </>
                );
              } else {
                return (
                  <>
                    <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <span className="text-blue-800 font-medium">{`${availableRooms.length} of ${roomTypes.length} rooms available`}</span>
                  </>
                );
              }
            })()}
          </div>
        </div>
      )}

      {/* Minimum Stay Warning */}
      {availability && availability.data?.roomtypes && (() => {
        const roomTypes = availability.data.roomtypes;
        const nightsCount = getNightsCount();
        const roomsWithMinStay = roomTypes.filter(room => 
          room.minlos && room.minlos > nightsCount
        );
        
        if (roomsWithMinStay.length > 0) {
          const maxMinStay = Math.max(...roomsWithMinStay.map(room => room.minlos));
          return (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-orange-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                </svg>
                <span className="text-orange-800 font-medium">
                  Some rooms require a minimum stay of {maxMinStay} night{maxMinStay > 1 ? 's' : ''}. 
                  Consider extending your stay for more options.
                </span>
              </div>
            </div>
          );
        }
        return null;
      })()}

      {/* Room Types Display */}
      {propertyData && propertyData.roomtypes && (
        <div className="space-y-8 mt-8 max-w-[900px] mx-auto">
          <h2 className={`text-3xl font-bold text-center mb-8 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Available Room Types</h2>
          
          {(() => {
            console.log('Availability data:', availability);
            console.log('Property data roomtypes:', propertyData.roomtypes);
            if (availability && availability.data?.roomtypes) {
              console.log('Availability roomtypes:', availability.data.roomtypes);
            }
            return null;
          })()}
          
          {propertyData.roomtypes.map((roomType, index) => (
            <div 
              key={roomType.roomtypeid} 
              onClick={() => {
                // Check if room is available before opening booking modal
                if (availability && availability.data?.roomtypes) {
                  const matchingRoom = availability.data.roomtypes.find(room => 
                    room.rtid === roomType.roomtypeid || room.roomtypeid === roomType.roomtypeid
                  );
                  
                  if (matchingRoom) {
                    const isAvailable = matchingRoom.availability && Array.isArray(matchingRoom.availability) 
                      ? matchingRoom.availability.every(day => day.roomsfree === true)
                      : matchingRoom.available !== false;
                    
                    if (isAvailable) {
                      // Trigger the booking modal with room type ID
                      window.dispatchEvent(new CustomEvent('openBookingModal', {
                        detail: { roomTypeId: roomType.roomtypeid }
                      }));
                    }
                  }
                } else {
                  // If no availability data, allow booking (default behavior)
                  window.dispatchEvent(new CustomEvent('openBookingModal', {
                    detail: { roomTypeId: roomType.roomtypeid }
                  }));
                }
              }}
              className={`rounded-lg shadow-lg overflow-hidden transition-all duration-300 cursor-pointer border border-black ${
                (() => {
                  if (!availability || !availability.data?.roomtypes) {
                    return false; // Default to not dimmed if no availability data
                  }
                  
                  const matchingRoom = availability.data.roomtypes.find(room => 
                    room.rtid === roomType.roomtypeid || room.roomtypeid === roomType.roomtypeid
                  );
                  
                  if (!matchingRoom) {
                    return false; // Default to not dimmed if no matching room
                  }
                  
                  // Use the same logic as the original RoomsPage
                  if (matchingRoom.availability && Array.isArray(matchingRoom.availability)) {
                    // Real API response structure: check if ALL days in the range are available
                    const allDaysAvailable = matchingRoom.availability.every(day => day.roomsfree === true);
                    return !allDaysAvailable; // Return true to dim if not all days available
                  } else if (matchingRoom.available !== undefined) {
                    // Old mock data structure
                    return matchingRoom.available === false; // Return true to dim if explicitly false
                  }
                  
                  return false; // Default to not dimmed if no data
                })()
                  ? 'opacity-50 grayscale' 
                  : 'hover:shadow-xl hover:scale-[1.02]'
              } ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}
            >
              <div className="md:flex">
                {/* Room Image */}
                <div className="md:w-1/3">
                  <div className="h-48 md:h-full bg-gray-200 flex items-center justify-center">
                    <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z"></path>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5a2 2 0 012-2h4a2 2 0 012 2v2H8V5z"></path>
                    </svg>
                  </div>
                </div>

                {/* Room Details */}
                <div className="md:w-2/3 p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className={`text-xl font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                        {roomType.roomtypename || roomType.rtname || 'Room'}
                      </h3>
                      <div className="space-y-1">
                        <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          <span className="font-medium">Max Occupancy:</span> {(() => {
                            if (availability && availability.data?.roomtypes) {
                              const matchingRoom = availability.data.roomtypes.find(room => 
                                room.rtid === roomType.roomtypeid || room.roomtypeid === roomType.roomtypeid
                              );
                              return matchingRoom?.maxoccupancy || '2';
                            }
                            return '2';
                          })()} guests
                        </p>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          <span className="font-medium">Max Adults:</span> {(() => {
                            if (availability && availability.data?.roomtypes) {
                              const matchingRoom = availability.data.roomtypes.find(room => 
                                room.rtid === roomType.roomtypeid || room.roomtypeid === roomType.roomtypeid
                              );
                              return matchingRoom?.maxadults || '2';
                            }
                            return '2';
                          })()}
                        </p>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          <span className="font-medium">Rooms Available:</span> {(() => {
                            if (availability && availability.data?.roomtypes) {
                              const matchingRoom = availability.data.roomtypes.find(room => 
                                room.rtid === roomType.roomtypeid || room.roomtypeid === roomType.roomtypeid
                              );
                              return matchingRoom?.roomsfree || '1';
                            }
                            return '1';
                          })()}
                        </p>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          <span className="font-medium">Rate Scheme:</span> {(() => {
                            if (availability && availability.data?.roomtypes) {
                              const matchingRoom = availability.data.roomtypes.find(room => 
                                room.rtid === roomType.roomtypeid || room.roomtypeid === roomType.roomtypeid
                              );
                              return matchingRoom?.rateschemedesc || 'Per Person Sharing';
                            }
                            return 'Per Person Sharing';
                          })()}
                        </p>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          <span className="font-medium">Bed Count:</span> {(() => {
                            if (availability && availability.data?.roomtypes) {
                              const matchingRoom = availability.data.roomtypes.find(room => 
                                room.rtid === roomType.roomtypeid || room.roomtypeid === roomType.roomtypeid
                              );
                              return matchingRoom?.bedcount || '1';
                            }
                            return '1';
                          })()}
                        </p>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          <span className="font-medium">Room Size:</span> {(() => {
                            if (availability && availability.data?.roomtypes) {
                              const matchingRoom = availability.data.roomtypes.find(room => 
                                room.rtid === roomType.roomtypeid || room.roomtypeid === roomType.roomtypeid
                              );
                              return matchingRoom?.roomsizeinmeters ? `${matchingRoom.roomsizeinmeters}m²` : '35m²';
                            }
                            return '35m²';
                          })()}
                        </p>
                      </div>
                      {(() => {
                        // Get minimum stay from availability data if available
                        if (availability && availability.data?.roomtypes) {
                          const matchingRoom = availability.data.roomtypes.find(room => 
                            room.rtid === roomType.roomtypeid || room.roomtypeid === roomType.roomtypeid
                          );
                          if (matchingRoom && matchingRoom.minlos && matchingRoom.minlos > 0) {
                            return (
                              <p className={`text-sm ${isDarkMode ? 'text-orange-300' : 'text-orange-600'}`}>
                                Minimum Stay: {matchingRoom.minlos} night{matchingRoom.minlos > 1 ? 's' : ''}
                              </p>
                            );
                          }
                        }
                        return null;
                      })()}
                    </div>
                    
                    {/* Availability Badge */}
                    <div className="flex flex-col items-end">
                      {availability && availability.data?.roomtypes && (
                        <span className={`px-3 py-1 rounded-full text-xs font-medium mb-2 ${
                          (() => {
                            const matchingRoom = availability.data.roomtypes.find(room => 
                              room.rtid === roomType.roomtypeid || room.roomtypeid === roomType.roomtypeid
                            );
                            
                            if (!matchingRoom) return 'bg-gray-100 text-gray-800';
                            
                            if (matchingRoom.availability && Array.isArray(matchingRoom.availability)) {
                              const allDaysAvailable = matchingRoom.availability.every(day => day.roomsfree === true);
                              return allDaysAvailable ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
                            } else if (matchingRoom.available !== undefined) {
                              return matchingRoom.available !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
                            }
                            
                            return 'bg-gray-100 text-gray-800';
                          })()
                        }`}>
                          {(() => {
                            const matchingRoom = availability.data.roomtypes.find(room => 
                              room.rtid === roomType.roomtypeid || room.roomtypeid === roomType.roomtypeid
                            );
                            
                            if (!matchingRoom) return 'Unknown';
                            
                            if (matchingRoom.availability && Array.isArray(matchingRoom.availability)) {
                              const allDaysAvailable = matchingRoom.availability.every(day => day.roomsfree === true);
                              return allDaysAvailable ? 'Available' : 'Unavailable';
                            } else if (matchingRoom.available !== undefined) {
                              return matchingRoom.available !== false ? 'Available' : 'Unavailable';
                            }
                            
                            return 'Unknown';
                          })()}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Room Amenities */}
                  {roomType.amenities && roomType.amenities.length > 0 && (
                    <div className="mb-4">
                      <h4 className={`text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Room Amenities</h4>
                      <div className="flex flex-wrap gap-2">
                        {roomType.amenities.slice(0, 3).map((amenity, idx) => (
                          <span key={idx} className={`px-2 py-1 rounded-full text-xs ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'text-gray-700'}`} style={!isDarkMode ? { backgroundColor: '#f6f2ec' } : {}}>
                            {amenity.description}
                          </span>
                        ))}
                        {roomType.amenities.length > 3 && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium text-blue-600">
                            +{roomType.amenities.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Room Description */}
                  {roomType.description && (
                    <div className="mb-4">
                      <h4 className={`text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Description</h4>
                      <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {roomType.description.split(' ').slice(0, 8).join(' ')}
                        {roomType.description.split(' ').length > 8 && '...'}
                      </p>
                    </div>
                  )}

                  {/* Booking Button */}
                  <div className="flex justify-between items-center">
                    <div>
                      <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                        {(() => {
                          // Try to get pricing from availability data first
                          if (availability && availability.data?.roomtypes) {
                            const matchingRoom = availability.data.roomtypes.find(room => 
                              room.rtid === roomType.roomtypeid || room.roomtypeid === roomType.roomtypeid
                            );
                            
                            if (matchingRoom && matchingRoom.mealplans && matchingRoom.mealplans.length > 0) {
                              const defaultMealPlan = matchingRoom.mealplans.find(mp => mp.defaultmealplan) || matchingRoom.mealplans[0];
                              if (defaultMealPlan && defaultMealPlan.rates && defaultMealPlan.rates.length > 0) {
                                return `From R${defaultMealPlan.rates[0]}`;
                              }
                            }
                            
                            // Also check for rates directly in the room data
                            if (matchingRoom && matchingRoom.rates && matchingRoom.rates.length > 0) {
                              return `From R${matchingRoom.rates[0]}`;
                            }
                          }
                          
                          // Fallback to pricing data if available
                          const roomId = roomType.roomtypeid;
                          if (pricingData && pricingData[roomId] && pricingData[roomId].roomtypes) {
                            const pricingRoom = pricingData[roomId].roomtypes.find(rt => 
                              rt.rtid === roomType.roomtypeid || rt.roomtypeid === roomType.roomtypeid
                            );
                            
                            if (pricingRoom && pricingRoom.mealplans && pricingRoom.mealplans.length > 0) {
                              const defaultMealPlan = pricingRoom.mealplans.find(mp => mp.defaultmealplan) || pricingRoom.mealplans[0];
                              if (defaultMealPlan && defaultMealPlan.rates && defaultMealPlan.rates.length > 0) {
                                return `From R${defaultMealPlan.rates[0]}`;
                              }
                            }
                          }
                          
                          // Final fallback
                          return `From R${roomType.rate || 'N/A'}`;
                        })()}
                      </p>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>per night</p>
                    </div>
                    <button
                      className={`px-6 py-2 rounded-full font-medium transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                        (() => {
                          if (!availability || !availability.data?.roomtypes) return 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500';
                          
                          const matchingRoom = availability.data.roomtypes.find(room => 
                            room.rtid === roomType.roomtypeid || room.roomtypeid === roomType.roomtypeid
                          );
                          
                          if (!matchingRoom) return 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500';
                          
                          if (matchingRoom.availability && Array.isArray(matchingRoom.availability)) {
                            const allDaysAvailable = matchingRoom.availability.every(day => day.roomsfree === true);
                            return allDaysAvailable ? 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500' : 'bg-gray-400 text-gray-600 cursor-not-allowed';
                          } else if (matchingRoom.available !== undefined) {
                            return matchingRoom.available !== false ? 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500' : 'bg-gray-400 text-gray-600 cursor-not-allowed';
                          }
                          
                          return 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500';
                        })()
                      }`}
                      disabled={(() => {
                        if (!availability || !availability.data?.roomtypes) return false;
                        
                        const matchingRoom = availability.data.roomtypes.find(room => 
                          room.rtid === roomType.roomtypeid || room.roomtypeid === roomType.roomtypeid
                        );
                        
                        if (!matchingRoom) return false;
                        
                        if (matchingRoom.availability && Array.isArray(matchingRoom.availability)) {
                          const allDaysAvailable = matchingRoom.availability.every(day => day.roomsfree === true);
                          return !allDaysAvailable;
                        } else if (matchingRoom.available !== undefined) {
                          return matchingRoom.available === false;
                        }
                        
                        return false;
                      })()}
                    >
                      {(() => {
                        if (!availability || !availability.data?.roomtypes) return 'Book Now';
                        
                        const matchingRoom = availability.data.roomtypes.find(room => 
                          room.rtid === roomType.roomtypeid || room.roomtypeid === roomType.roomtypeid
                        );
                        
                        if (!matchingRoom) return 'Book Now';
                        
                        if (matchingRoom.availability && Array.isArray(matchingRoom.availability)) {
                          const allDaysAvailable = matchingRoom.availability.every(day => day.roomsfree === true);
                          return allDaysAvailable ? 'Book Now' : 'Unavailable';
                        } else if (matchingRoom.available !== undefined) {
                          return matchingRoom.available !== false ? 'Book Now' : 'Unavailable';
                        }
                        
                        return 'Book Now';
                      })()}
                    </button>
                  </div>
                                  </div>
                </div>
              </div>
            ))}
        </div>
      )}


    </div>
  );
};

export default DateRangePicker; 